import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { task, timeout } from 'ember-concurrency';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{this.updateModal}}\n\n<div class=\"modal-container {{this.showClass}} {{if @tinyModal \'tiny-modal\'}} {{if @warningModal \'warning-modal\'}}\" style=\"{{css-style \'z-index\' this.zIndexStyle}}\">\n	{{#if @showModal}}\n		<div class=\"modal-background\">\n			<div class=\"modal\" role=\"dialog\" aria-modal=\"true\" aria-labelledby=\"beebee-modal-title-{{this.elementId}}\">\n				<div class=\"modal-bar modal-header\" style=\"{{css-style \'z-index\' this.zIndexStyle}}\">\n					<h4 id=\"beebee-modal-title-{{this.elementId}}\">{{@title}}</h4>\n					{{#if this.showCloseButton}}\n						<button class=\"close-button hdr-btn\" type=\"button\" aria-label=\"{{t \'close\'}}\" {{on \"click\" this.closeModal}} {{on-key \'Escape\'}}>\n							<FaIcon @icon=\"times\" aria-hidden=\"true\" />\n							<EmberTooltip @popperContainer=\'body\'>{{t \'close\'}}</EmberTooltip>\n						</button>\n					{{/if}}\n				</div>\n				{{#if this.showAlert}}\n					<div class=\"force-close-modal\">\n						<h5>{{t \'Unsaved data\'}}</h5>\n						<div class=\"grid-x\">\n							<div class=\"cell small-12 medium-auto\">\n								<button class=\"button expanded alert\" type=\"button\" {{on \"click\" this.forceCloseModal}} {{on-key \'Y\'}}>\n									{{t \'close without saving\'}}\n								</button>\n							</div>\n							<div class=\"cell small-12 medium-auto\">\n								<span class=\"button expanded\" {{on \"click\" (fn (mut this.showAlert) null)}}>\n									{{t \'stay on this page\'}}\n								</span>\n							</div>\n						</div>\n					</div>\n				{{/if}}\n				{{#if (has-block \'body\')}}\n					{{!-- Named-block mode: component owns body/footer wrappers --}}\n					<div class=\"content-bar modal-body {{if (has-block \'footer\') \'content-footer\'}}\">\n						{{yield to=\'body\'}}\n					</div>\n					{{#if (has-block \'footer\')}}\n						<div class=\"modal-bar modal-footer\" style=\"{{css-style \'z-index\' this.zIndexStyle}}\">\n							{{yield to=\'footer\'}}\n						</div>\n					{{/if}}\n				{{else if @fullContent}}\n					<div class=\"content-bar\">\n						<div class=\"grid grid-padding-y\">\n							<div class=\"cell auto\">\n								{{yield}}\n							</div>\n						</div>\n					</div>\n				{{else}}\n					{{!-- Legacy mode: consumer ships .content-bar + .modal-footer inside default block --}}\n					{{yield}}\n				{{/if}}\n			</div>\n		</div>\n	{{/if}}\n</div>\n");

var _dec, _dec2, _class, _descriptor, _descriptor2;
const ANIMATION_DELAY_SHORT = 50;
const ANIMATION_DELAY_MEDIUM = 150;

/**
 * CSS class constants
 * @constant {string}
 */
const CLASS_IN = 'in';
const CLASS_OUT = 'out';
const CLASS_MODAL_OPEN = 'modal-open';

/**
 * Default z-index value for the modal
 * @constant {string}
 */
const DEFAULT_Z_INDEX = '10';

/**
 * BeebeeModal Component
 *
 * A modal dialog component with animation support, scroll preservation,
 * and dirty state detection for unsaved changes.
 *
 * @class BbModalComponent
 * @extends Component
 *
 * @arg {boolean} showModal - Controls modal visibility
 * @arg {Array} [arrayModal] - Alternative array-based visibility control
 * @arg {Function} closeModal - Callback invoked when modal closes
 * @arg {Object} [item] - Data model to check for dirty state
 * @arg {string} [zIndex='10'] - Custom z-index for the modal
 * @arg {boolean} [showCloseButton=true] - Whether to show the close button
 */
let BbModalComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  restartable: true
}), _class = class BbModalComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Stable per-instance id used for the dialog's aria-labelledby target.
     * @type {string}
     */
    this.elementId = guidFor(this);
    /**
     * Whether to show the unsaved changes alert
     * @type {boolean}
     */
    _initializerDefineProperty(this, "showAlert", _descriptor, this);
    /**
     * Current visibility class for the modal ('in' or 'out')
     * @type {string}
     */
    _initializerDefineProperty(this, "showClass", _descriptor2, this);
    /**
     * Stored scroll position to restore after modal closes
     * @type {number}
     */
    this.bodyScrollTop = 0;
  }
  /**
   * Returns the z-index style value
   * @returns {string}
   */
  get zIndexStyle() {
    return this.args.zIndex ?? DEFAULT_Z_INDEX;
  }

  /**
   * Whether to display the close button
   * @returns {boolean}
   */
  get showCloseButton() {
    return this.args.showCloseButton ?? true;
  }

  /**
   * Checks if the modal is currently visible
   * @returns {boolean}
   */
  get isVisible() {
    return this.showClass === CLASS_IN;
  }

  /**
   * Checks if modal should be shown based on showModal or arrayModal args
   * @returns {boolean}
   * @private
   */
  get _shouldShowModal() {
    const {
      showModal,
      arrayModal
    } = this.args;
    return showModal || Array.isArray(arrayModal) && arrayModal.length > 0;
  }

  /**
   * Reactive getter that triggers modal open/close based on showModal arg.
   * Note: This getter has side effects by design for template reactivity.
   * @returns {null}
   */
  get updateModal() {
    if (this.args.showModal) {
      this.upTask.perform();
    } else if (this.isVisible) {
      this.closeTask.perform();
    }
    return null;
  }

  /**
   * Safely adds a class to the document body
   * @param {string} className - The class to add
   * @private
   */
  _addBodyClass(className) {
    document.body?.classList.add(className);
  }

  /**
   * Safely removes a class from the document body
   * @param {string} className - The class to remove
   * @private
   */
  _removeBodyClass(className) {
    document.body?.classList.remove(className);
  }

  /**
   * Safely invokes the closeModal callback if provided
   * @private
   */
  _invokeCloseModal() {
    if (typeof this.args.closeModal === 'function') {
      this.args.closeModal();
    }
  }

  /**
   * Gets the current scroll position
   * @returns {number}
   * @private
   */
  _getScrollTop() {
    return window.pageYOffset || document.documentElement?.scrollTop || 0;
  }

  /**
   * Scrolls the window to a specific position
   * @param {number} top - The scroll position
   * @private
   */
  _scrollTo(top) {
    window.scrollTo(0, top);
  }

  /**
   * Task to open the modal with animation
   * @task
   */
  *upTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    if (!this._shouldShowModal) {
      this.closeTask.perform();
      return;
    }
    this.bodyScrollTop = this._getScrollTop();
    this._addBodyClass(CLASS_MODAL_OPEN);
    this._scrollTo(this.bodyScrollTop);
    this.showClass = CLASS_IN;
    this.showAlert = false;
  }

  /**
   * Task to close the modal with animation
   * @task
   */
  *closeTask() {
    yield timeout(ANIMATION_DELAY_SHORT);
    if (this.isDestroying || this.isDestroyed) return;
    this._removeBodyClass(CLASS_MODAL_OPEN);
    this._scrollTo(this.bodyScrollTop);
    this.showClass = CLASS_OUT;
    yield timeout(ANIMATION_DELAY_MEDIUM);
    if (this.isDestroying || this.isDestroyed) return;
    this._invokeCloseModal();
  }

  /**
   * Ensures modal-open class is always removed when component is destroyed,
   * even if the parent tears it down via {{#if}} before closeTask can run.
   */
  willDestroy() {
    super.willDestroy(...arguments);
    this._removeBodyClass(CLASS_MODAL_OPEN);
    this._scrollTo(this.bodyScrollTop);
    // Cancella timeout di animazione per evitare update su component distrutto
    this.upTask?.cancelAll?.();
    this.closeTask?.cancelAll?.();
  }

  /**
   * Action to close the modal, checking for unsaved changes first
   */
  closeModal() {
    document.activeElement?.blur();
    if (!this.args.showModal) {
      return;
    }
    const hasDirtyAttributes = this.args.item?.hasDirtyAttributes ?? false;
    if (hasDirtyAttributes) {
      this.showAlert = true;
    } else {
      this.closeTask.perform();
    }
  }

  /**
   * Action to force close the modal, rolling back any unsaved changes
   */
  forceCloseModal() {
    const {
      item
    } = this.args;
    if (this.showAlert && item?.id) {
      item.rollbackAttributes?.();
      item.safeReload?.();
    }
    this.closeTask.perform();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "showAlert", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "showClass", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return CLASS_OUT;
  }
}), _applyDecoratedDescriptor(_class.prototype, "upTask", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "upTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "closeTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "closeModal"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "forceCloseModal", [action], Object.getOwnPropertyDescriptor(_class.prototype, "forceCloseModal"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, BbModalComponent);

export { BbModalComponent as default };
//# sourceMappingURL=bb-modal.js.map
